import { api } from "@/lib/api";

export type ReportedWaitBucket =
  | "under_2w"
  | "w2_6"
  | "w6_12"
  | "over_3m"
  | "still_waiting";

export type WaitReportOutcome = "accepted" | "declined" | "no_reply";

export interface GpReported {
  n: number;
  typicalBucket: ReportedWaitBucket;
  acceptedShare: number;
  lastReportedAt: string;
}

export interface WaitReportPayload {
  facilityId: string;
  seenBucket: ReportedWaitBucket;
  outcome: WaitReportOutcome;
  referredMonth: string;
  email: string;
}

export interface WaitReportVerifyPeek {
  facilityName: string;
  seenBucket: ReportedWaitBucket;
  /** legacy alias — some responses used `bucket` */
  bucket?: ReportedWaitBucket;
  status?: "pending" | "verified" | "expired";
}

export const REPORTED_WAIT_BUCKET_LABELS: Record<ReportedWaitBucket, string> = {
  under_2w: "Under 2 wks",
  w2_6: "2–6 wks",
  w6_12: "6–12 wks",
  over_3m: "Over 3 months",
  still_waiting: "Still waiting",
};

export const WAIT_REPORT_OUTCOME_LABELS: Record<WaitReportOutcome, string> = {
  accepted: "Accepted",
  declined: "Declined",
  no_reply: "No reply",
};

export const REPORTED_WAIT_BUCKET_OPTIONS = typedEntries(REPORTED_WAIT_BUCKET_LABELS);
export const WAIT_REPORT_OUTCOME_OPTIONS = typedEntries(WAIT_REPORT_OUTCOME_LABELS);

export function submitWaitReport(citySlug: string, payload: WaitReportPayload) {
  return api.post<{ ok: true }>(`/v1/public/${citySlug}/wait-reports`, payload, {
    skipAuth: true,
  });
}

export function peekWaitReport(citySlug: string, token: string) {
  return api.get<WaitReportVerifyPeek>(
    `/v1/public/${citySlug}/wait-reports/verify/${encodeURIComponent(token)}`,
    { skipAuth: true },
  );
}

export function verifyWaitReport(citySlug: string, token: string) {
  return api.post<WaitReportVerifyPeek>(
    `/v1/public/${citySlug}/wait-reports/verify/${encodeURIComponent(token)}`,
    undefined,
    { skipAuth: true },
  );
}

export function compactGpReportedLabel(report: GpReported): string {
  return `GP-reported: typically ${reportedWaitBucketLabel(report.typicalBucket)} (n=${report.n})`;
}

export function fullGpReportedLabel(report: GpReported): string {
  const accepted = Math.min(report.n, Math.max(0, Math.round(report.acceptedShare * report.n)));
  return `GP-reported: typically ${reportedWaitBucketLabel(
    report.typicalBucket,
  )} · ${accepted} of ${report.n} accepted · n=${report.n}`;
}

export function reportedWaitBucketLabel(bucket: ReportedWaitBucket | null | undefined): string {
  if (!bucket) return "wait not recorded";
  return (REPORTED_WAIT_BUCKET_LABELS[bucket] ?? String(bucket)).toLowerCase();
}

function typedEntries<T extends string>(labels: Record<T, string>) {
  return (Object.entries(labels) as Array<[T, string]>).map(([value, label]) => ({
    value,
    label,
  }));
}
